import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Baby, Heart, Moon, Sun, Clock, Utensils, AlertCircle, Save, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '../components/common/Logo';
import MoonIcon from '../components/common/MoonIcon';
import ProgressSteps from '../components/common/ProgressSteps';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { NativeRadioGroup, NativeRadioGroupItem } from '../components/ui/native-radio-group';
import { howBabySleepsOptions, whereBabySleepsOptions } from '../data/mockData';
import apiService, { convertReportToFrontend } from '../services/api';

// Componente Select compatível com iOS - SEMPRE usa select nativo para evitar bugs do Radix UI
const MobileSelect = ({ value, onValueChange, options, placeholder = "Selecione..." }) => {
  // SEMPRE usar select nativo para máxima compatibilidade com iOS/Safari
  return (
    <select
      value={value || ''}
      onChange={(e) => onValueChange(e.target.value)}
      className="flex h-10 w-full items-center justify-between rounded-md border border-[#FAE0D5] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8F7AC5] focus:border-transparent appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%238F7AC5%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:0.65em] bg-[right_0.7rem_center] bg-no-repeat"
    >
      <option value="" disabled>{placeholder}</option>
      {options.map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
};

const ReportFormPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [lastSaved, setLastSaved] = useState(null);
  const [showSaveNotice, setShowSaveNotice] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingReport, setLoadingReport] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [customFields, setCustomFields] = useState([]);

  // Definir getEmptyFormData PRIMEIRO (antes de ser usada)
  const getEmptyFormData = () => ({
    // Dados básicos
    babyName: '',
    babyAge: '',
    birthWeeks: '',
    birthDate: '',
    motherName: '',
    fatherName: '',
    email: '',
    whatsapp: '',
    // Questionário inicial
    sleepDifficulty: '',
    expectations: '',
    previousAttempts: '',
    healthConditions: '',
    heelPrickTest: '',
    snoring: '',
    ironSupplementation: '',
    feedingType: '',
    solidFoods: '',
    motorDevelopment: '',
    howBabySleeps: '',
    sleepLocation: '',
    transitionObject: '',
    transitionObjectDesc: '',
    caregiver: '',
    otherChildren: '',
    motherSleep: '',
    familyStructure: ''
  });

  // Helper seguro para localStorage (iOS Safari modo privado pode falhar)
  const safeLocalStorage = {
    getItem: (key) => {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn('localStorage not available:', e);
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        console.warn('localStorage not available:', e);
        return false;
      }
    }
  };

  // Carregar dados salvos do localStorage OU do backend
  const getInitialData = () => {
    const saved = safeLocalStorage.getItem(`form_${reportId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          currentStep: parsed.currentStep || 1,
          formData: parsed.formData || getEmptyFormData()
        };
      } catch {
        return { currentStep: 1, formData: getEmptyFormData() };
      }
    }
    return { currentStep: 1, formData: getEmptyFormData() };
  };

  const initialData = getInitialData();
  const [currentStep, setCurrentStep] = useState(initialData.currentStep);
  const [formData, setFormData] = useState(initialData.formData);

  const totalSteps = 4;
  const stepLabels = ['Boas-vindas', 'Dados', 'Sobre o Sono', 'Saúde'];

  // Carregar dados do backend ao iniciar
  useEffect(() => {
    const loadFormConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/form-config`);
        const config = await response.json();
        setFormConfig(config);
        
        // Extrair campos customizados ativos
        const activeCustomFields = (config.custom_fields || []).filter(f => f.active !== false);
        setCustomFields(activeCustomFields);
        
        // Atualizar formData para incluir campos customizados
        const customFieldsDefaults = {};
        activeCustomFields.forEach(f => {
          customFieldsDefaults[f.field_key] = '';
        });
        setFormData(prev => ({ ...prev, ...customFieldsDefaults }));
      } catch (error) {
        console.error('Erro ao carregar configuração do formulário:', error);
      }
    };
    
    loadFormConfig();
  }, []);

  useEffect(() => {
    const loadReportFromBackend = async () => {
      try {
        setLoadingReport(true);
        const report = await apiService.getReport(reportId);
        const converted = convertReportToFrontend(report);
        
        if (converted) {
          // Mesclar dados do backend com dados locais (priorizar backend se existirem)
          setFormData(prev => ({
            ...prev,
            babyName: converted.babyName || prev.babyName,
            babyAge: converted.babyAge || prev.babyAge,
            birthWeeks: converted.birthWeeks || prev.birthWeeks,
            birthDate: converted.birthDate || prev.birthDate,
            motherName: converted.motherName || prev.motherName,
            fatherName: converted.fatherName || prev.fatherName,
            email: converted.email || prev.email,
            whatsapp: converted.whatsapp || prev.whatsapp,
            // Dados do formulário inicial
            ...(converted.initialForm || {}),
          }));
        }
      } catch (error) {
        console.log('Relatório não encontrado no backend, usando dados locais:', error.message);
      } finally {
        setLoadingReport(false);
      }
    };

    loadReportFromBackend();
  }, [reportId]);

  // Salvar automaticamente quando dados mudam (localStorage + Backend)
  useEffect(() => {
    const saveData = async () => {
      // Sempre salvar no localStorage para backup (usando helper seguro para iOS)
      const dataToSave = {
        formData,
        currentStep,
        lastSaved: new Date().toISOString()
      };
      safeLocalStorage.setItem(`form_${reportId}`, JSON.stringify(dataToSave));
      setLastSaved(new Date());
      
      // Também salvar no backend para sincronização entre dispositivos
      // Só salva se há dados significativos preenchidos
      const hasData = formData.babyName || formData.motherName || formData.email || 
                      formData.sleepDifficulty || formData.expectations;
      if (hasData && reportId) {
        try {
          await apiService.updateReportBasicInfo(reportId, formData);
          await apiService.updateInitialForm(reportId, formData);
        } catch (error) {
          console.error('Erro no auto-save backend:', error);
        }
      }
    };

    const debounceTimer = setTimeout(saveData, 2000); // 2 segundos de debounce
    return () => clearTimeout(debounceTimer);
  }, [formData, currentStep, reportId]);

  // Função para salvar no backend
  const saveToBackend = async () => {
    setSaving(true);
    setApiError(null);
    
    try {
      // Salvar dados básicos
      await apiService.updateReportBasicInfo(reportId, formData);
      
      // Salvar formulário inicial
      await apiService.updateInitialForm(reportId, formData);
      
      setLastSaved(new Date());
      return true;
    } catch (error) {
      console.error('Erro ao salvar no backend:', error);
      setApiError('Erro ao salvar. Os dados estão salvos localmente.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Scroll para o topo ao mudar de step
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const nextStep = async () => {
    // Validação obrigatória no Step 2 (Dados do Bebê)
    if (currentStep === 2) {
      const errors = [];
      if (!formData.babyName || formData.babyName.trim() === '') {
        errors.push('Nome do bebê');
      }
      if (!formData.babyAge || formData.babyAge.toString().trim() === '') {
        errors.push('Idade do bebê');
      }
      if (!formData.birthWeeks || formData.birthWeeks.toString().trim() === '') {
        errors.push('Semanas de gestação');
      }
      if (!formData.birthDate || formData.birthDate.trim() === '') {
        errors.push('Data de nascimento');
      }
      if (!formData.motherName || formData.motherName.trim() === '') {
        errors.push('Nome da mãe');
      }
      if (!formData.fatherName || formData.fatherName.trim() === '') {
        errors.push('Nome do pai');
      }
      if (!formData.email || formData.email.trim() === '') {
        errors.push('E-mail');
      }
      if (!formData.whatsapp || formData.whatsapp.trim() === '') {
        errors.push('WhatsApp');
      }
      
      if (errors.length > 0) {
        toast.error(`Por favor, preencha os campos obrigatórios: ${errors.join(', ')}`, {
          duration: 5000,
        });
        return;
      }
    }
    
    // Validação obrigatória no Step 3 (Sobre o Sono)
    if (currentStep === 3) {
      const errors = [];
      if (!formData.sleepDifficulty || formData.sleepDifficulty.trim() === '') {
        errors.push('Dificuldade com o sono');
      }
      if (!formData.expectations || formData.expectations.trim() === '') {
        errors.push('Expectativas');
      }
      if (!formData.previousAttempts || formData.previousAttempts.trim() === '') {
        errors.push('Técnicas já tentadas');
      }
      if (!formData.howBabySleeps || formData.howBabySleeps.trim() === '') {
        errors.push('Como o bebê adormece');
      }
      if (!formData.sleepLocation || formData.sleepLocation.trim() === '') {
        errors.push('Onde o bebê dorme');
      }
      if (!formData.transitionObject) {
        errors.push('Objeto de transição');
      }
      
      if (errors.length > 0) {
        toast.error(`Por favor, preencha os campos obrigatórios: ${errors.join(', ')}`, {
          duration: 5000,
        });
        return;
      }
    }
    
    // Validação obrigatória no Step 4 (Saúde) - antes de finalizar
    if (currentStep === 4) {
      const errors = [];
      if (!formData.healthConditions || formData.healthConditions.trim() === '') {
        errors.push('Condições de saúde');
      }
      if (!formData.heelPrickTest || formData.heelPrickTest.trim() === '') {
        errors.push('Teste do pezinho');
      }
      if (!formData.snoring || formData.snoring.trim() === '') {
        errors.push('Ronco na madrugada');
      }
      if (!formData.ironSupplementation || formData.ironSupplementation.trim() === '') {
        errors.push('Suplementação de ferro e vitamina D');
      }
      if (!formData.feedingType || formData.feedingType.trim() === '') {
        errors.push('Tipo de alimentação');
      }
      if (!formData.solidFoods || formData.solidFoods.trim() === '') {
        errors.push('Introdução alimentar');
      }
      if (!formData.motorDevelopment || formData.motorDevelopment.trim() === '') {
        errors.push('Desenvolvimento motor');
      }
      if (!formData.familyStructure || formData.familyStructure.trim() === '') {
        errors.push('Estrutura familiar');
      }
      if (!formData.caregiver || formData.caregiver.trim() === '') {
        errors.push('Quem cuida do bebê');
      }
      if (!formData.otherChildren || formData.otherChildren.trim() === '') {
        errors.push('Outros filhos');
      }
      if (!formData.motherSleep || formData.motherSleep.trim() === '') {
        errors.push('Sono da mãe');
      }
      
      // Validar campos customizados obrigatórios desta etapa
      const customFieldsStep4 = customFields.filter(f => f.step === 4 && f.required && f.active !== false);
      customFieldsStep4.forEach(field => {
        const value = formData[field.field_key];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          errors.push(field.label);
        }
      });
      
      if (errors.length > 0) {
        toast.error(`Por favor, preencha os campos obrigatórios: ${errors.join(', ')}`, {
          duration: 5000,
        });
        return;
      }
    }
    
    // Validar campos customizados em outras etapas
    const customFieldsCurrentStep = customFields.filter(f => f.step === currentStep && f.required && f.active !== false);
    const customErrors = [];
    customFieldsCurrentStep.forEach(field => {
      const value = formData[field.field_key];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        customErrors.push(field.label);
      }
    });
    
    if (customErrors.length > 0 && currentStep !== 4) { // Step 4 já validado acima
      toast.error(`Por favor, preencha os campos obrigatórios: ${customErrors.join(', ')}`, {
        duration: 5000,
      });
      return;
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      scrollToTop();
    } else {
      // Finalizar formulário inicial - salvar no backend antes de ir para registro de rotina
      setSaving(true);
      const saved = await saveToBackend();
      setSaving(false);
      
      if (saved) {
        navigate(`/report/${reportId}/routine`);
      } else {
        // Mesmo com erro, permitir continuar (dados estão no localStorage)
        if (window.confirm('Houve um erro ao salvar no servidor. Deseja continuar mesmo assim? Seus dados estão salvos localmente.')) {
          navigate(`/report/${reportId}/routine`);
        }
      }
    }
  };

  const prevStep = async () => {
    // Salvar dados antes de voltar
    await saveToBackend();
    
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      scrollToTop();
    }
  };

  // Função para renderizar campos customizados dinamicamente
  const renderCustomField = (field) => {
    const value = formData[field.field_key] || '';
    const onChange = (newValue) => updateField(field.field_key, newValue);
    
    switch (field.field_type) {
      case 'textarea':
        return (
          <Textarea
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
          />
        );
      
      case 'email':
        return (
          <Input
            type="email"
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
          />
        );
      
      case 'tel':
        return (
          <Input
            type="tel"
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
          />
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
          />
        );
      
      case 'select':
        return (
          <MobileSelect 
            value={value} 
            onValueChange={onChange}
            options={field.options || []}
            placeholder={field.placeholder || 'Selecione...'}
          />
        );
      
      case 'radio':
        return (
          <NativeRadioGroup value={value} onValueChange={onChange} className="flex gap-4 mt-2">
            {(field.options || []).map(opt => (
              <NativeRadioGroupItem key={opt} value={opt} id={`${field.field_key}-${opt}`}>
                <Label htmlFor={`${field.field_key}-${opt}`}>{opt}</Label>
              </NativeRadioGroupItem>
            ))}
          </NativeRadioGroup>
        );
      
      case 'checkbox':
        const selectedValues = value ? value.split(',').filter(Boolean) : [];
        return (
          <div className="flex flex-wrap gap-4 mt-2">
            {(field.options || []).map(opt => (
              <div key={opt} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id={`${field.field_key}-${opt}`}
                  checked={selectedValues.includes(opt)}
                  onChange={(e) => {
                    let newValues;
                    if (e.target.checked) {
                      newValues = [...selectedValues, opt];
                    } else {
                      newValues = selectedValues.filter(v => v !== opt);
                    }
                    onChange(newValues.join(','));
                  }}
                  className="rounded border-[#FAE0D5]"
                />
                <Label htmlFor={`${field.field_key}-${opt}`}>{opt}</Label>
              </div>
            ))}
          </div>
        );
      
      default: // text
        return (
          <Input
            type="text"
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
          />
        );
    }
  };

  // Mostrar tela de loading enquanto carrega dados do backend
  if (loadingReport) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-white flex flex-col items-center justify-center">
        <Logo variant="v3SonoRotina" size="md" />
        <div className="mt-8 flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-[#8F7AC5] animate-spin" />
          <p className="mt-4 text-[#6B5B8C] text-sm">Carregando questionário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-white">
      {/* Header */}
      <header className="py-4 px-4 border-b border-[#FAE0D5]">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Logo variant="v3SonoRotina" size="sm" />
          <div className="flex items-center gap-2">
            {lastSaved && (
              <span className="text-xs text-[#7BC47F] flex items-center gap-1">
                <Save className="w-3 h-3" />
                Salvo
              </span>
            )}
            <span className="text-sm text-[#8F7AC5] font-medium">Questionário Inicial</span>
          </div>
        </div>
      </header>

      {/* Aviso de salvamento automático */}
      <>
        {showSaveNotice && currentStep === 1 && (
          <div
            
            
            
            className="max-w-2xl mx-auto px-4 pt-4"
          >
            <div className="bg-[#8F7AC5]/10 border border-[#8F7AC5]/20 rounded-2xl p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-[#8F7AC5] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-[#30244F] font-medium">Suas informações são salvas automaticamente! 💜</p>
                <p className="text-xs text-[#6B5B8C] mt-1">
                  Pode fechar a página tranquila. Quando voltar, continuará de onde parou.
                </p>
              </div>
              <button 
                onClick={() => setShowSaveNotice(false)}
                className="text-[#9890A8] hover:text-[#30244F] text-sm"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <ProgressSteps currentStep={currentStep} totalSteps={totalSteps} labels={stepLabels} />
      </div>

      {/* Form Content */}
      <main className="max-w-2xl mx-auto px-4 pb-40 mb-16">
        <>
          <div
            key={currentStep}
          >
            {/* Step 1: Welcome */}
            {currentStep === 1 && (
              <div className="text-center py-8 space-y-6">
                <div>
                  <MoonIcon variant="purple" size="xl" className="mx-auto" />
                </div>
                
                <div className="space-y-4">
                  <h1 className="text-3xl font-bold text-[#30244F]">
                    Seja bem-vinda! 💜
                  </h1>
                  <p className="text-[#6B5B8C] text-lg max-w-md mx-auto">
                    Ficaremos juntas nessa jornada em busca do melhor sono possível 
                    para você e sua família.
                  </p>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-sm max-w-md mx-auto">
                  <h3 className="font-semibold text-[#30244F] mb-4">Como funciona:</h3>
                  <div className="space-y-3 text-left">
                    {[
                      'Responda algumas perguntas sobre seu bebê',
                      'Registre a rotina por 5 dias',
                      'Receba seu plano de sono personalizado'
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#D98DAE]/30 flex items-center justify-center text-[#8F7AC5] font-semibold text-sm">
                          {i + 1}
                        </div>
                        <span className="text-[#6B5B8C]">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-sm text-[#9890A8]">
                  Tempo estimado: 10-15 minutos
                </p>
              </div>
            )}

            {/* Step 2: Basic Data */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <Baby className="w-12 h-12 text-[#8F7AC5] mx-auto mb-3" />
                  <h2 className="text-2xl font-bold text-[#30244F]">Dados do Bebê</h2>
                  <p className="text-[#6B5B8C]">Conte-nos sobre seu pequeno(a)</p>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-[#30244F]">Nome do bebê <span className="text-red-500">*</span></Label>
                      <Input 
                        placeholder="Como ele(a) se chama?" 
                        value={formData.babyName}
                        onChange={(e) => updateField('babyName', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5] focus:ring-[#8F7AC5]/20"
                        required
                        data-testid="baby-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#30244F]">Idade (meses) <span className="text-red-500">*</span></Label>
                      <Input 
                        type="number" 
                        placeholder="Ex: 8"
                        value={formData.babyAge}
                        onChange={(e) => updateField('babyAge', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                        required
                        data-testid="baby-age-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#30244F]">Nasceu com quantas semanas? <span className="text-red-500">*</span></Label>
                      <Input 
                        type="number" 
                        placeholder="Ex: 39"
                        value={formData.birthWeeks}
                        onChange={(e) => updateField('birthWeeks', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[#30244F]">Data de nascimento <span className="text-red-500">*</span></Label>
                      <Input 
                        type="date"
                        value={formData.birthDate}
                        onChange={(e) => updateField('birthDate', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
                  <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                    <Heart className="w-5 h-5 text-[#D98DAE]" />
                    Dados da Família
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#30244F]">Nome da mãe <span className="text-red-500">*</span></Label>
                      <Input 
                        placeholder="Seu nome"
                        value={formData.motherName}
                        onChange={(e) => updateField('motherName', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#30244F]">Nome do pai <span className="text-red-500">*</span></Label>
                      <Input 
                        placeholder="Nome do pai"
                        value={formData.fatherName}
                        onChange={(e) => updateField('fatherName', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#30244F]">E-mail <span className="text-red-500">*</span></Label>
                      <Input 
                        type="email"
                        placeholder="seu@email.com"
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#30244F]">WhatsApp <span className="text-red-500">*</span></Label>
                      <Input 
                        type="tel"
                        placeholder="(11) 99999-9999"
                        value={formData.whatsapp}
                        onChange={(e) => updateField('whatsapp', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Campos Customizados - Etapa 2 */}
                {customFields.filter(f => f.step === 2 && f.active !== false).length > 0 && (
                  <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
                    <h3 className="font-semibold text-[#30244F]">Informações Adicionais</h3>
                    {customFields.filter(f => f.step === 2 && f.active !== false).map(field => (
                      <div key={field.id || field.field_key}>
                        <Label className="text-[#30244F]">
                          {field.label}
                          {field.required && <span className="text-red-500"> *</span>}
                        </Label>
                        {renderCustomField(field)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Sleep Info */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <Moon className="w-12 h-12 text-[#8F7AC5] mx-auto mb-3" />
                  <h2 className="text-2xl font-bold text-[#30244F]">Sobre o Sono</h2>
                  <p className="text-[#6B5B8C]">Entender a situação atual é fundamental</p>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
                  <div>
                    <Label className="text-[#30244F]">Qual a maior dificuldade com o sono do seu bebê? <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Descreva o que mais te preocupa ou incomoda..."
                      value={formData.sleepDifficulty}
                      onChange={(e) => updateField('sleepDifficulty', e.target.value)}
                      className="mt-1 min-h-[100px] border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Quais são suas expectativas com a mentoria? <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="O que você espera alcançar?"
                      value={formData.expectations}
                      onChange={(e) => updateField('expectations', e.target.value)}
                      className="mt-1 min-h-[80px] border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Já tentou alguma técnica? O que funcionou ou não? <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Conte suas experiências anteriores..."
                      value={formData.previousAttempts}
                      onChange={(e) => updateField('previousAttempts', e.target.value)}
                      className="mt-1 min-h-[80px] border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
                  <h3 className="font-semibold text-[#30244F]">Hábitos atuais</h3>
                  
                  <div>
                    <Label className="text-[#30244F]">Como o bebê adormece atualmente? <span className="text-red-500">*</span></Label>
                    <MobileSelect 
                      value={formData.howBabySleeps} 
                      onValueChange={(v) => updateField('howBabySleeps', v)}
                      options={howBabySleepsOptions}
                      placeholder="Selecione..."
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Onde o bebê dorme? <span className="text-red-500">*</span></Label>
                    <MobileSelect 
                      value={formData.sleepLocation} 
                      onValueChange={(v) => updateField('sleepLocation', v)}
                      options={whereBabySleepsOptions}
                      placeholder="Selecione..."
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Usa objeto de transição? (naninha, fraldinha...) <span className="text-red-500">*</span></Label>
                    <NativeRadioGroup 
                      value={formData.transitionObject} 
                      onValueChange={(v) => updateField('transitionObject', v)}
                      className="flex gap-4 mt-2"
                    >
                      <NativeRadioGroupItem value="sim" id="obj-sim">
                        <Label htmlFor="obj-sim">Sim</Label>
                      </NativeRadioGroupItem>
                      <NativeRadioGroupItem value="nao" id="obj-nao">
                        <Label htmlFor="obj-nao">Não</Label>
                      </NativeRadioGroupItem>
                    </NativeRadioGroup>
                    {formData.transitionObject === 'sim' && (
                      <Input 
                        placeholder="Qual objeto?"
                        value={formData.transitionObjectDesc}
                        onChange={(e) => updateField('transitionObjectDesc', e.target.value)}
                        className="mt-2 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      />
                    )}
                  </div>
                </div>
                
                {/* Campos Customizados - Etapa 3 */}
                {customFields.filter(f => f.step === 3 && f.active !== false).length > 0 && (
                  <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
                    <h3 className="font-semibold text-[#30244F]">Informações Adicionais</h3>
                    {customFields.filter(f => f.step === 3 && f.active !== false).map(field => (
                      <div key={field.id || field.field_key}>
                        <Label className="text-[#30244F]">
                          {field.label}
                          {field.required && <span className="text-red-500"> *</span>}
                        </Label>
                        {renderCustomField(field)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Health */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <AlertCircle className="w-12 h-12 text-[#8F7AC5] mx-auto mb-3" />
                  <h2 className="text-2xl font-bold text-[#30244F]">Saúde e Contexto</h2>
                  <p className="text-[#6B5B8C]">Informações importantes para personalizar o plano</p>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
                  <div>
                    <Label className="text-[#30244F]">O bebê possui alguma condição especial de saúde? <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Descreva ou escreva 'Não possui'"
                      value={formData.healthConditions}
                      onChange={(e) => updateField('healthConditions', e.target.value)}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#30244F]">Teste do pezinho <span className="text-red-500">*</span></Label>
                      <Input 
                        placeholder="Normal / Alterado"
                        value={formData.heelPrickTest}
                        onChange={(e) => updateField('heelPrickTest', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#30244F]">Ronca na madrugada? <span className="text-red-500">*</span></Label>
                      <Input 
                        placeholder="Sim / Não / Às vezes"
                        value={formData.snoring}
                        onChange={(e) => updateField('snoring', e.target.value)}
                        className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Suplementa ferro e vitamina D? <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Se sim, qual horário faz a suplementação? Algum problema com ferro na gestação?"
                      value={formData.ironSupplementation}
                      onChange={(e) => updateField('ironSupplementation', e.target.value)}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      rows={2}
                    />
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
                  <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                    <Utensils className="w-5 h-5 text-[#D98DAE]" />
                    Alimentação
                  </h3>
                  
                  <div>
                    <Label className="text-[#30244F]">Tipo de alimentação <span className="text-red-500">*</span></Label>
                    <Input 
                      placeholder="Aleitamento materno / Fórmula / Misto"
                      value={formData.feedingType}
                      onChange={(e) => updateField('feedingType', e.target.value)}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Introdução alimentar <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Já começou? Como está sendo?"
                      value={formData.solidFoods}
                      onChange={(e) => updateField('solidFoods', e.target.value)}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
                  <h3 className="font-semibold text-[#30244F]">Desenvolvimento e Contexto</h3>
                  
                  <div>
                    <Label className="text-[#30244F]">Desenvolvimento motor atual <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Rola, engatinha, anda, escala..."
                      value={formData.motorDevelopment}
                      onChange={(e) => updateField('motorDevelopment', e.target.value)}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Qual a estrutura familiar? <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Com quem o bebê mora? (ex: pai, mãe, avós, etc.)"
                      value={formData.familyStructure}
                      onChange={(e) => updateField('familyStructure', e.target.value)}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Quem cuida do bebê? Tem ajuda? <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Descreva sua rede de apoio..."
                      value={formData.caregiver}
                      onChange={(e) => updateField('caregiver', e.target.value)}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Tem mais filhos? Como é a dinâmica? <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Conte sobre a rotina da casa..."
                      value={formData.otherChildren}
                      onChange={(e) => updateField('otherChildren', e.target.value)}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Como está o seu sono? Está afetando seu bem-estar? <span className="text-red-500">*</span></Label>
                    <Textarea 
                      placeholder="Seja honesta, isso é importante para o plano..."
                      value={formData.motherSleep}
                      onChange={(e) => updateField('motherSleep', e.target.value)}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>
                  
                  {/* Campos Customizados - Etapa 4 */}
                  {customFields.filter(f => f.step === 4 && f.active !== false).map(field => (
                    <div key={field.id || field.field_key}>
                      <Label className="text-[#30244F]">
                        {field.label}
                        {field.required && <span className="text-red-500"> *</span>}
                      </Label>
                      {renderCustomField(field)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      </main>

      {/* Fixed Bottom Navigation */}
      <div className="fixed bottom-14 left-0 right-0 bg-white border-t border-[#FAE0D5] p-4 z-40">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="text-[#6B5B8C]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          <Button
            onClick={nextStep}
            className="bg-[#8F7AC5] hover:bg-[#30244F] text-white px-8"
          >
            {currentStep === totalSteps ? (
              <>
                Iniciar Registro
                <Check className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Continuar
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportFormPage;
